import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getItem, getContent, asset } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { Reveal } from '@/components/Reveal';
import { Icon } from '@/components/Icons';
import { MagneticButton } from '@/components/MagneticButton';
import { Loader } from '@/components/Loader';
import { CtaSection } from '@/components/CtaSection';
import { ArrowUpRight, MapPin, Check } from 'lucide-react';

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setService(null); setNotFound(false);
    getItem('services', slug).then(setService).catch(() => setNotFound(true));
    getContent('services').then(setServices).catch(() => {});
    getContent('projects').then(setProjects).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className='flex min-h-[70vh] flex-col items-center justify-center bg-[#20272A] text-center'>
        <p className='eyebrow text-[#B88A3B]'>Service not found</p>
        <Link to='/services' className='mt-4 text-[#F6F5F1] underline'>Back to services</Link>
      </div>
    );
  }
  if (!service) return <Loader label='Loading service' />;

  const keys = [service.title, service.category, ...(service.capabilities || [])].join(' ').toLowerCase();
  let related = projects.filter((p) => !p.placeholder && (p.serviceCategory || []).some((sc) => keys.includes(sc.toLowerCase()) || sc.toLowerCase().includes(service.title.toLowerCase())));
  if (!related.length) related = projects.filter((p) => !p.placeholder && p.featured);
  related = related.slice(0, 3);

  const others = services.filter((s) => s.slug !== service.slug).slice(0, 6);

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Service', name: service.title,
    serviceType: service.category, provider: { '@type': 'Organization', name: 'Sri Lakshmi Enterprises' },
    areaServed: 'Tamil Nadu', description: service.tagline,
  };

  return (
    <>
      <Seo title={service.title} description={service.tagline} path={`/services/${service.slug}`} image={service.image} jsonLd={jsonLd} />
      <PageHero
        eyebrow={service.category}
        title={service.title}
        subtitle={service.tagline}
        image={service.image}
        meta={<MagneticButton to='/contact' variant='gold' data-cursor='ENQUIRE'>Discuss This Requirement</MagneticButton>}
      />

      <section className='bg-[#F6F5F1] py-20 lg:py-28'>
        <div className='container-x grid gap-14 lg:grid-cols-12'>
          <div className='lg:col-span-7'>
            <Reveal>
              <span className='eyebrow text-[#075B61]'>What We Do</span>
              <p className='mt-4 text-lg leading-relaxed text-[#20272A]/75'>{service.whatWeDo}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <div className='mt-12'>
                <span className='eyebrow text-[#075B61]'>Our Approach</span>
                <p className='mt-4 leading-relaxed text-[#20272A]/70'>{service.approach}</p>
              </div>
            </Reveal>
          </div>
          <div className='lg:col-span-5'>
            <div className='rounded-2xl border border-[#20272A]/10 bg-white p-8'>
              <span className='eyebrow text-[#075B61]'>Where It Matters</span>
              <ul className='mt-5 space-y-3'>
                {(service.whereItMatters || []).map((w) => (
                  <li key={w} className='flex items-start gap-3 text-sm text-[#20272A]/75'>
                    <Check size={16} className='mt-0.5 shrink-0 text-[#075B61]' /> {w}
                  </li>
                ))}
              </ul>
              <div className='mt-8 border-t border-[#20272A]/10 pt-6'>
                <span className='eyebrow text-[#075B61]'>Capabilities</span>
                <div className='mt-4 flex flex-wrap gap-2'>
                  {(service.capabilities || []).map((c) => (
                    <span key={c} className='rounded-full border border-[#20272A]/12 px-3 py-1 text-xs text-[#20272A]/70'>{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment / capability */}
      {service.equipment?.length > 0 && (
        <section className='relative bg-[#20272A] py-20'>
          <div className='grain absolute inset-0' />
          <div className='relative container-x'>
            <SectionHeading eyebrow='Equipment / Capability' title='The machinery behind the work.' light />
            <div className='mt-10 flex flex-wrap gap-3'>
              {service.equipment.map((e) => (
                <span key={e} className='inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3 text-sm text-[#F6F5F1]/80'>
                  <Icon name='truck' size={18} className='text-[#B88A3B]' /> {e}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project applications */}
      {related.length > 0 && (
        <section className='bg-[#F6F5F1] py-20 lg:py-28'>
          <div className='container-x'>
            <SectionHeading eyebrow='Project Applications' title='Where this capability delivered.' />
            <div className='mt-12 grid gap-6 md:grid-cols-3'>
              {related.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} data-cursor='VIEW PROJECT' className='group block overflow-hidden rounded-2xl border border-[#20272A]/10 bg-white'>
                  <div className='relative h-52 overflow-hidden'>
                    <img src={asset(p.heroImage)} alt={p.projectTitle} className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105' />
                  </div>
                  <div className='p-5'>
                    <div className='flex items-center gap-1.5 text-xs text-[#20272A]/50'><MapPin size={13} className='text-[#B88A3B]' /> {p.location}</div>
                    <h3 className='mt-1.5 font-head text-lg font-semibold text-[#20272A]'>{p.associatedBrand || p.projectTitle}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Result / value + other services */}
      <section className='relative overflow-hidden bg-[#101517] py-16'>
        <div className='container-x'>
          <div className='rounded-2xl border border-white/10 bg-[#20272A] p-8'>
            <span className='eyebrow text-[#B88A3B]'>Result / Value</span>
            <p className='mt-3 max-w-2xl text-[#F6F5F1]/70'>Measurable project results are added per engagement. <span className='text-[#F6F5F1]/45'>Content to be updated.</span></p>
          </div>
          <div className='mt-12'>
            <span className='eyebrow text-[#F6F5F1]/50'>Other Services</span>
            <div className='mt-5 flex flex-wrap gap-3'>
              {others.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className='group inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm text-[#F6F5F1]/75 transition-colors hover:border-[#B88A3B]/60 hover:text-[#F6F5F1]'>
                  {s.title} <ArrowUpRight size={14} className='text-[#B88A3B]' />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
